import { useContext, useEffect, useMemo, useState } from 'react';
import { TextureLoader } from 'three';
import * as THREE from 'three';
import CurrentContext from './CurrentContext';
import { useControls } from 'leva';
import useKeyPressToggle from './useKeyPressToggle';
// import { getMaterialsData } from "../dbApis";

/**
 * Custom hook for applying and updating textures to a material based on provided finish data.
 *
 * This hook:
 * - Finds the appropriate finish data from a predefined list.
 * - Loads textures asynchronously using the `TextureLoader`.
 * - Applies loaded textures and updates material properties such as color, roughness, and metalness.
 * - Resets material properties when no texture URL is provided.
 * - Cleans up by disposing of textures when the component unmounts or dependencies change.
 *
 * @param {THREE.Material} material - The material to which textures and properties will be applied.
 * @param {string} finishes - ID of the finish data to be applied.
 * @returns {THREE.TextureLoader} - The texture loader instance used for loading textures.
 */

export default function useSetMaterialFinish(
  material,
  applyFinish,
  factor,
  aoMapUrl,
  aoIntensity,
) {
  //     const [finishesData, setFinishesData] =  useState([]);

  //     useEffect(() => {
  //     const fetchMaterialsData = async () => {
  //       const materialsData = await getMaterialsData();
  //       setFinishesData(materialsData);
  //     };
  //     fetchMaterialsData();
  //   }, []);

  // console.log(material,finishes)
  // Find the finish data that matches the provided finishes Name
  //   const applyFinish = finishesData && finishesData.find((item) => item.name === finishes);
  // console.log(applyFinish,"applyFinishapplyFinishapplyFinishapplyFinish")

  // const { repeatX, repeatY, offsetX, offsetY } = useControls({
  //   repeatX: { value: 1, min: 1, max: 10, step: 0.01 },
  //   repeatY: { value: 1, min: 1, max: 10, step: 0.01 },
  //   offsetX: { value: 0, min: 0, max: 1, step: 0.01 },
  //   offsetY: { value: 0, min: 0, max: 1, step: 0.01 },
  // });
  const { isAo } = useKeyPressToggle();

  const {
    diffuse,
    normalMap,
    aoMapIntensity,
    metalness,
    normal,
    offset,
    repeat,
    roughness,
    visible,
    clearcoat,
    clearcoatRoughness,
    rotation,
    color,
  } = applyFinish || {};
  // Memoize the TextureLoader to avoid recreating it on every render
  const loader = useMemo(() => new TextureLoader(), []);
  // console.log(material,"material")

  useEffect(() => {
    // Exit early if material is not provided
    if (!material) return;

    // Array to keep track of loaded textures
    const textures = [];

    // Function to load a texture asynchronously
    const loadTextureAsync = async (url, settings, isSrgb) => {
      return new Promise((resolve) => {
        loader.load(url, (texture) => {
          // Set texture properties
          texture.flipY = false;
          if (isSrgb) {
            texture.colorSpace = THREE.SRGBColorSpace;
          }
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(settings[0], settings[1]);
          texture.rotation = THREE.MathUtils.degToRad(rotation);

          // Add texture to the array and resolve the promise
          textures.push(texture);
          resolve(texture);
        });
      });
    };

    // Function to load a texture asynchronously
    const loadAOAsync = async (url, settings, isSrgb) => {
      return new Promise((resolve) => {
        loader.load(url, (texture) => {
          // Set texture properties
          texture.flipY = false;
          if (isSrgb) {
            texture.colorSpace = THREE.SRGBColorSpace;
          }
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.repeat.set(settings[0], settings[1]);
          // texture.rotation = THREE.MathUtils.degToRad(rotation);

          // Add texture to the array and resolve the promise
          textures.push(texture);
          resolve(texture);
        });
      });
    };

    // Function to update the material with textures and properties
    const updateMaterialTextures = async () => {
      // Array to hold promises for loading textures
      const texturePromises = [];

      // Reset material properties if the material name does not match the check list
      material.roughnessMap = null;
      material.metalnessMap = null;
      material.aoMap = null;
      // material.normalScale.set(1, 1);
      material.roughness = roughness; // Default roughness value
      material.metalness = metalness; // Default metalness value
      material.clearcoat = clearcoat; // Default clearcoat value
      material.clearcoatRoughness = clearcoatRoughness; // Default clearcoat roughness value
      material.ior = 1; // Default index of refraction value
      // console.log(repeat)
      // console.log(factor.reapeatFactor);
      //  console.log(factor.offsetFactor);
      const newRepeat = repeat?.map(
        (value, index) => value * factor.reapeatFactor[index],
      );
      const newOffset = offset?.map(
        (value, index) => value + factor.offsetFactor[index],
      );

      // console.log(newRepeat,"newRepeat");

      // Load and apply diffuse texture if URL is provided
      if (diffuse) {
        texturePromises.push(
          loadTextureAsync(diffuse, newRepeat ? newRepeat : [1, 1], true).then(
            (texture) => {
              material.map = texture;
              material.color = new THREE.Color(color);
              if (newOffset) {
                texture.offset.set(newOffset[0], newOffset[1]);
              }
            },
          ),
        );
      }
      if (diffuse === null && color) {
        material.map = null;
        material.color = new THREE.Color(color);
        if (material.name === 'paint') {
          material.emissive = new THREE.Color(color);
        }
      }

      if (normalMap) {
        texturePromises.push(
          loadTextureAsync(
            normalMap,
            newRepeat ? newRepeat : [1, 1],
            false,
          ).then((texture) => {
            material.normalMap = texture;
          }),
        );
      }

      // Load and apply AO map using the second UV channel (UVMap.001)

      if (aoMapUrl && isAo) {
        texturePromises.push(
          loadAOAsync(
            '../textures/ao/' + aoMapUrl + '.jpg',
            [1, 1],
            false,
          ).then((texture) => {
            material.aoMap = texture;
            material.aoMapIntensity = aoIntensity;
            material.aoMap.offset.set(0, 0);
            material.aoMap.channel = 1; // Use second UV channel (UVMap.001)
          }),
        );
      } else {
        material.aomap = null;
      }

      // Set material properties if values are provided
      if (roughness !== undefined) material.roughness = roughness;
      if (metalness !== undefined) material.metalness = metalness;

      // Wait for all textures to be loaded and applied
      await Promise.all(texturePromises);

      // Mark material as needing update to reflect changes
      material.needsUpdate = true;
    };

    // Call the function to update material textures
    updateMaterialTextures();

    // Cleanup function to dispose of textures when component unmounts or dependencies change
    return () => {
      textures.forEach((texture) => {
        texture.dispose(); // Dispose of each texture
      });
    };
  }, [
    diffuse,
    normalMap,
    aoMapIntensity,
    metalness,
    normal,
    offset,
    repeat,
    roughness,
    visible,
    clearcoat,
    clearcoatRoughness,
    factor,
    rotation,
    color,
    aoMapUrl,
    isAo,
    // postProcess,
    // repeatX,
    // repeatY,
    // offsetX,
    // offsetY,
  ]);

  return loader;
}
